import { z } from "zod";
export const roles = [
    "admin",
    "backoffice",
    "counter",
    "receiving",
    "sorting",
    "supervisor",
    "portal",
];
const text = z.string().trim().min(1).max(250), id = z.string().min(1).max(100);
const qty = z.number().int().min(0).max(1000000);
const maybeQty = qty.nullable().default(null);
const optionalText = z.string().max(2000).default("");
export const locationSchema = z
    .object({
    id,
    name: text,
    kind: z.enum(["Ward", "Central holding", "Other"]),
    wardType: optionalText,
    beds: maybeQty,
    occupancy: z.number().min(0).max(100).nullable().default(null),
    items: z.array(id),
    allocations: z.record(qty).default({}),
    targets: z.record(qty).default({}),
    safety: z.record(qty).default({}),
    active: z.boolean().default(true),
})
    .strict();
export const configSchema = z
    .object({
    name: text,
    code: text,
    active: z.boolean().default(true),
    contact: optionalText,
    email: z.string().email().or(z.literal("")).default(""),
    phone: optionalText,
    address: optionalText,
    legalName: optionalText,
    taxReference: optionalText,
    postalAddress: optionalText,
    contacts: z
        .array(z
        .object({
        id,
        name: text,
        role: optionalText,
        email: z.string().email().or(z.literal("")).default(""),
        phone: optionalText,
        siteIds: z.array(id).default([]),
        active: z.boolean().default(true),
    })
        .strict())
        .default([]),
    sites: z
        .array(z
        .object({
        id,
        name: text,
        model: z.enum(["Linen Management", "Wash & Return"]),
        policy: z.enum([
            "Accept customer",
            "Verify customer",
            "Full laundry count",
        ]),
        countMode: z.enum(["Ward", "Bulk"]),
        locations: z.array(locationSchema).min(1),
        active: z.boolean().default(true),
    })
        .strict())
        .min(1),
    items: z
        .array(z
        .object({
        id,
        name: text,
        description: optionalText,
        active: z.boolean().default(true),
    })
        .strict())
        .min(1),
    packing: z
        .object({
        prepackPercent: z.number().min(0).max(100).nullable().default(null),
        instructions: optionalText,
    })
        .default({}),
    billing: z
        .object({
        basis: optionalText,
        periods: optionalText,
        cutoff: optionalText,
        rateDate: z
            .enum(["Delivery date", "Contract exception"])
            .default("Delivery date"),
        contractException: optionalText,
        rates: z
            .array(z.object({
            itemId: id,
            rate: z.number().min(0),
            effectiveFrom: z.string().date(),
            currency: z.string().length(3),
        }))
            .default([]),
        exceptionCharges: optionalText,
    })
        .default({}),
    delivery: z
        .object({
        slipRequired: z.boolean().default(false),
        instructions: optionalText,
    })
        .default({}),
    portal: z
        .object({
        enabled: z.boolean().default(false),
        showCounter: z.boolean().default(true),
    })
        .default({}),
    study: z
        .object({
        reference: optionalText,
        sufficientStock: z.boolean().nullable().default(null),
    })
        .default({}),
})
    .strict();
export const lineSchema = z
    .object({ itemId: id, parts: z.array(qty).max(500), estimated: maybeQty })
    .strict();
export const countSchema = z
    .object({
    locationId: id,
    status: z.enum([
        "Not Started",
        "In Progress",
        "Completed",
        "Completed with Estimate/Exception",
    ]),
    lines: z.array(lineSchema).min(1),
    assistants: z.array(id).max(30).default([]),
    bags: maybeQty,
    yellowBags: maybeQty,
    unmarkedYellow: z.boolean().default(false),
    comment: optionalText,
    internalComment: optionalText,
    specialRequest: optionalText,
    startedAt: z.string().datetime(),
    finishedAt: z.string().datetime().nullable().default(null),
    interactions: z.array(z.string().datetime()).max(10000).default([]),
})
    .strict();
export const receiptSchema = z
    .object({
    slip: optionalText,
    // Legacy compatibility only: preserve historic values and pre-change queued captures.
    // New soiled receipt screens never capture clean delivery quantities.
    cleanBags: qty.nullable().optional(),
    soiledBags: maybeQty,
    driver: optionalText,
    assistant: optionalText,
    vehicle: optionalText,
    arrival: z.string().datetime().nullable().default(null),
    departure: z.string().datetime().nullable().default(null),
    km: z.number().min(0).nullable().default(null),
    comment: optionalText,
    internalComment: optionalText,
    allocations: z
        .array(z
        .object({
        countId: id,
        countVersion: z.number().int().positive(),
        itemId: id,
        quantity: qty,
        estimatedQuantity: qty.default(0),
    })
        .strict())
        .default([]),
})
    .strict();
export const sortingSchema = z
    .object({
    receiptId: id,
    status: z.enum([
        "In Progress",
        "Completed",
        "Completed with Estimate/Exception",
    ]),
    assistants: z.array(id).default([]),
    bags: maybeQty,
    lines: z
        .array(z.object({ itemId: id, locationId: id.optional(), quantity: maybeQty }))
        .default([]),
    comment: optionalText,
    internalComment: optionalText,
    startedAt: z.string().datetime(),
    finishedAt: z.string().datetime().nullable().default(null),
    interactions: z.array(z.string().datetime()).max(10000).default([]),
})
    .strict();
export const verificationSchema = z
    .object({
    receiptId: id,
    basis: z.enum([
        "Accepted customer count",
        "Verified laundry count",
        "Full laundry count",
    ]),
    lines: z
        .array(z
        .object({
        itemId: id,
        quantity: qty,
        approvedEstimate: qty.default(0),
        locationId: id.optional(),
        includesSeptic: z.boolean().default(false),
    })
        .strict())
        .min(1),
    reason: text,
})
    .strict();
export const commandSchema = z
    .object({
    requestId: z.string().uuid(),
    resolutionOf: z.string().uuid().optional(),
    entityId: z.string().uuid(),
    kind: z.enum([
        "config",
        "count",
        "receipt",
        "sorting",
        "verification",
        "cleanup",
        "variance",
    ]),
    customerId: id,
    siteId: id.optional(),
    configId: id.optional(),
    configVersion: z.number().int().positive().optional(),
    expectedVersion: z.number().int().min(0),
    occurredAt: z.string().datetime(),
    reason: optionalText,
    payload: z.unknown(),
})
    .strict();
export class RuleError extends Error {
    status;
    constructor(status, message) {
        super(message);
        this.status = status;
    }
}
export function requireRule(ok, message, status = 422) {
    if (!ok)
        throw new RuleError(status, message);
}
export function authorize(actor, kind, site) {
    const grants = {
        config: ["backoffice"],
        count: ["counter", "supervisor", "backoffice"],
        receipt: ["receiving", "supervisor", "backoffice"],
        sorting: ["sorting", "supervisor", "backoffice"],
        verification: ["receiving", "sorting", "supervisor", "backoffice"],
        cleanup: ["supervisor", "backoffice"],
        variance: ["supervisor", "backoffice"],
    };
    requireRule(grants[kind]?.includes(actor.role), "Permission denied", 403);
    if (site) {
        requireRule(actor.sites.includes("*") || actor.sites.includes(site), "Site access denied", 403);
        if (actor.deviceSite)
            requireRule(site === actor.deviceSite, "This tablet is bound to another site", 403);
    }
}
export function canSee(actor, site) {
    return (actor.role !== "admin" &&
        (actor.sites.includes("*") || (!!site && actor.sites.includes(site))));
}
export function sumParts(parts) {
    return parts.reduce((a, b) => a + b, 0);
}
export function parseCalculator(value) {
    requireRule(/^\s*\d+(\s*\+\s*\d+)*\s*$/.test(value), "Use whole numbers separated by +");
    const parts = value.split("+").map(Number);
    parts.forEach((n) => requireRule(Number.isSafeInteger(n) && n <= 1000000, "Quantity out of range"));
    requireRule(sumParts(parts) <= 1000000, "Total out of range");
    return parts;
}
export function activity(start, end, interactions) {
    const a = Date.parse(start), b = end ? Date.parse(end) : Date.now();
    requireRule(b >= a, "Finish must follow start");
    const points = [a, ...interactions.map(Date.parse)]
        .filter((t) => t >= a && t <= b)
        .sort((x, y) => x - y);
    let active = 0, right = a;
    for (const t of points) {
        const stop = Math.min(t + 120000, b);
        active += Math.max(0, stop - Math.max(t, right));
        right = Math.max(right, stop);
    }
    return {
        elapsedSeconds: Math.floor((b - a) / 1000),
        activeSeconds: Math.floor(active / 1000),
        idleSeconds: Math.floor((b - a - active) / 1000),
    };
}
export function comparison(original, verified) {
    return verified === null
        ? "Awaiting verification"
        : verified === original
            ? "Checked and correct"
            : verified > original
                ? "Laundry count higher"
                : "Laundry count lower";
}
export function editablePayload(kind, payload) {
    const p = structuredClone(payload);
    for (const key of [
        "timing",
        "counter",
        "sorter",
        "washhouseAvailable",
        "reference",
        "approvedBy",
        "productivity",
        "receiptVersion",
        "coverage",
        "missingCoverage",
    ])
        delete p[key];
    if (kind === "verification")
        for (const l of p.lines) {
            delete l.original;
            delete l.operationalQuantity;
            delete l.comparison;
            delete l.comparisonBase;
        }
    if (kind === "receipt")
        for (const a of p.allocations)
            delete a.sourceLocationId;
    return p;
}
